import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { useAudioSettings } from '@/features/settings/hooks/useAudioSettings'
import type { AudioBackend, AudioEndpointDraft, AudioKind } from '@/features/settings/types'
import { ConnectionTestRow } from './ConnectionTestRow'

type AudioState = ReturnType<typeof useAudioSettings>

type EndpointEditorProps = {
  kind: AudioKind
  endpoint: AudioEndpointDraft
  onChange: (endpoint: AudioEndpointDraft) => void
}

function RemoteEndpointFields({ kind, endpoint, onChange }: EndpointEditorProps) {
  const { t } = useTranslation()
  return (
    <>
      <Label htmlFor={`${kind}-url`}>{t('settings.audio.url')}</Label>
      <Input
        id={`${kind}-url`}
        value={endpoint.url}
        onChange={event => onChange({ ...endpoint, url: event.target.value })}
        placeholder={`https://audio.xendrel.ai/v1/${kind === 'transcription' ? 'whisper' : 'diarization'}/jobs`}
      />
      <Label htmlFor={`${kind}-key`}>{t('settings.audio.key')}</Label>
      <Input
        id={`${kind}-key`}
        type="password"
        autoComplete="new-password"
        value={endpoint.api_key}
        placeholder={t(endpoint.has_api_key ? 'settings.audio.keepKey' : 'settings.audio.enterKey')}
        onChange={event => onChange({ ...endpoint, api_key: event.target.value })}
      />
    </>
  )
}

/** Transcription is always the WhisperX service; diarization is the Nemotron service or off (ADR-0072). */
function EndpointEditor({ kind, endpoint, onChange }: EndpointEditorProps) {
  const { t } = useTranslation()
  const title = t(`settings.audio.${kind}`)
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium">{title}</legend>
      {kind === 'diarization' && (
        <>
          <Label htmlFor={`${kind}-backend`}>{t('settings.audio.backend')}</Label>
          <Select
            value={endpoint.mode}
            onValueChange={mode => onChange({ ...endpoint, mode: mode as AudioBackend })}
          >
            <SelectTrigger id={`${kind}-backend`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="remote">{t('settings.audio.remote')}</SelectItem>
              <SelectItem value="disabled">{t('settings.audio.disabled')}</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}
      {endpoint.mode === 'remote' && (
        <RemoteEndpointFields kind={kind} endpoint={endpoint} onChange={onChange} />
      )}
    </fieldset>
  )
}

function EndpointSection({ kind, state }: { kind: AudioKind; state: AudioState }) {
  const { t } = useTranslation()
  const endpoint = state.draft?.[kind]
  if (!endpoint) return null
  return (
    <div className="space-y-3">
      <EndpointEditor
        kind={kind}
        endpoint={endpoint}
        onChange={endpoint => state.updateEndpoint(kind, endpoint)}
      />
      <ConnectionTestRow
        id={kind}
        result={state.testResults[kind]}
        testing={state.testingKind === kind}
        onTest={() => void state.test(kind)}
        labels={{ test: t('settings.audio.test'), working: t('settings.audio.working') }}
      />
    </div>
  )
}

export function AudioSection({ state }: { state: AudioState }) {
  const { t } = useTranslation()
  const { draft, busy, message, failed } = state
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.sections.audio')}</CardTitle>
        <CardDescription>{t('settings.audio.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {message && (
          <p
            role={failed ? 'alert' : 'status'}
            className={failed ? 'text-destructive' : 'text-muted-foreground'}
          >
            {message}
          </p>
        )}
        {!draft && !failed && <p role="status">{t('settings.audio.loading')}</p>}
        {draft && (
          <fieldset disabled={busy} className="space-y-6">
            {(['transcription', 'diarization'] as const).map(kind => (
              <EndpointSection key={kind} kind={kind} state={state} />
            ))}
            <p className="text-sm text-muted-foreground">{t('settings.audio.fallbacks')}</p>
            <Button type="button" onClick={() => void state.save()}>
              {t(busy && !state.testingKind ? 'settings.audio.working' : 'settings.audio.save')}
            </Button>
          </fieldset>
        )}
      </CardContent>
    </Card>
  )
}
